import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
  getSupplierSyncStageLabel,
  SupplierSyncEvent,
  SupplierSyncEventSource,
  SupplierSyncEventStatus,
} from '@dill/dill-shared';
import { Calendar } from 'phosphor-react';

import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getSupplierSyncHistory } from '../../../../redux/services/syncHistoryService';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillTable, DillColumn } from '../../../general/Dill/DillTable';
import AppDateRangePickerModal from '../../../general/AppDateRangePickerModal/AppDateRangePickerModal';

const SOURCE_LABELS: Record<SupplierSyncEventSource, string> = {
  AUTOMATIC: 'Automatic sync',
  INITIAL: 'Initial sync',
  EMAIL_UPLOAD: 'Emailed report',
  WEB_UPLOAD: 'Manual upload',
};

const STATUS_STYLES: Record<SupplierSyncEventStatus, { label: string; className: string }> = {
  SUCCEEDED: { label: 'Succeeded', className: 'bg-green-50 border-green-200 text-green-700' },
  PARTIAL: { label: 'Partial', className: 'bg-amber-50 border-amber-200 text-amber-700' },
  FAILED: { label: 'Failed', className: 'bg-red-50 border-red-200 text-red-700' },
  RUNNING: { label: 'In progress', className: 'bg-blue-50 border-blue-200 text-blue-700' },
};

type DateRange = { startDate: Date; endDate: Date };

const getDefaultDateRange = (): DateRange => ({
  startDate: moment().subtract(7, 'days').toDate(),
  endDate: new Date(),
});

const formatDuration = (row: SupplierSyncEvent) => {
  if (!row.finishedAt) return null;
  const seconds = moment(row.finishedAt).diff(moment(row.startedAt), 'seconds');
  if (seconds < 1) return null;
  if (seconds < 60) return `${seconds}s`;
  return moment.duration(seconds, 'seconds').humanize();
};

type SyncStageView = NonNullable<SupplierSyncEvent['stages']>[number];

const STAGE_DOT_CLASS: Record<SyncStageView['status'], string> = {
  COMPLETED: 'bg-green-500',
  FAILED: 'bg-red-500',
  RUNNING: 'bg-blue-500 animate-pulse',
};

// one line per row; the breakdown lives in the panel under the table
const getSummary = (row: SupplierSyncEvent) => {
  const stages = row.stages ?? [];
  if (row.status === 'RUNNING') {
    return {
      text: row.currentStage
        ? `Syncing ${getSupplierSyncStageLabel(row.currentStage).toLowerCase()}…`
        : 'Syncing…',
      className: 'text-blue-700',
    };
  }
  if (row.status === 'PARTIAL') {
    const failedCount = stages.filter((stage) => stage.status === 'FAILED').length;
    return {
      // a no-data sync is partial with every step completed, so the count would read "0 of N"
      text: failedCount
        ? `${failedCount} of ${stages.length} steps didn't sync`
        : row.failureMessage ?? 'Some data did not sync',
      className: 'text-amber-700',
    };
  }
  if (row.status === 'FAILED') {
    return { text: row.failureMessage ?? 'Sync failed', className: 'text-red-700' };
  }
  const records = stages.reduce((sum, stage) => sum + (stage.records ?? 0), 0);
  const duration = formatDuration(row);
  const parts = [
    duration && `Took ${duration}`,
    records > 0 && `${records.toLocaleString()} records`,
  ];
  return { text: parts.filter(Boolean).join(' · ') || '-', className: 'text-gray-500' };
};

const SyncEventDetails = ({ event }: { event: SupplierSyncEvent }) => {
  const stages = event.stages ?? [];
  return (
    <div className="flex flex-col gap-3 p-4 rounded-lg border border-gray-200 bg-gray-50 animate-fade-slide-in motion-reduce:animate-none">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-900">
          {SOURCE_LABELS[event.source] ?? event.source} ·{' '}
          {moment(event.startedAt).format('MMM D, YYYY h:mm a')}
        </span>
        {formatDuration(event) && (
          <span className="text-2xs text-gray-500">Took {formatDuration(event)}</span>
        )}
      </div>
      {event.status !== 'SUCCEEDED' && event.failureMessage && (
        <p className={`text-xs ${event.status === 'FAILED' ? 'text-red-700' : 'text-amber-700'}`}>
          {event.failureMessage}
        </p>
      )}
      {stages.length > 0 && (
        <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5">
          {stages.map((stage) => (
            <li key={stage.name} className="flex items-center gap-2 text-xs">
              <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 ${STAGE_DOT_CLASS[stage.status]}`}
              />
              <span
                className={
                  stage.status === 'FAILED' ? 'text-red-700 font-medium' : 'text-gray-700'
                }>
                {stage.label}
              </span>
              <span className="ml-auto text-gray-400 tabular-nums">
                {stage.status === 'FAILED'
                  ? 'Failed'
                  : stage.status === 'RUNNING'
                  ? 'In progress'
                  : stage.records
                  ? stage.records.toLocaleString()
                  : 'Done'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const SyncHistoryModal = ({
  open,
  onClose,
  supplierId,
}: {
  open: boolean;
  onClose: () => void;
  supplierId: string;
}) => {
  const dispatch = useAppDispatch();
  const { syncEvents, loadingList } = useAppSelector((state) => state.syncHistory);
  const isLoading = loadingList.includes('getSupplierSyncHistory');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  // the newest sync is what someone opening this usually wants to see
  const selectedEvent =
    syncEvents.find((event) => event.id === selectedEventId) ?? syncEvents[0] ?? null;

  useEffect(() => {
    if (!open) {
      setDateRange(getDefaultDateRange());
    }
  }, [open]);

  useEffect(() => {
    if (!open || !supplierId) return;
    setSelectedEventId(null);
    // whole days in the user's timezone; the server compares instants
    dispatch(
      getSupplierSyncHistory({
        supplierId,
        startDate: moment(dateRange.startDate).startOf('day').toISOString(),
        endDate: moment(dateRange.endDate).endOf('day').toISOString(),
      })
    );
  }, [open, supplierId, dateRange, dispatch]);

  // add up to 858: the 900 modal less DillModal's px-5 padding and the table border
  const columns = useMemo<DillColumn<SupplierSyncEvent>[]>(
    () => [
      {
        key: 'startedAt',
        label: 'Date',
        width: 180,
        sortable: true,
        getSortValue: (row) => moment(row.startedAt).valueOf(),
        render: (_value, row) => (
          <div className="flex flex-col">
            <span>{moment(row.startedAt).format('MMM D, YYYY h:mm a')}</span>
            <span className="text-2xs text-gray-400">{moment(row.startedAt).fromNow()}</span>
          </div>
        ),
      },
      {
        key: 'source',
        label: 'Type',
        width: 150,
        sortable: true,
        getSearchValue: (row) => SOURCE_LABELS[row.source] ?? row.source,
        render: (_value, row) => <span>{SOURCE_LABELS[row.source] ?? row.source}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        sortable: true,
        getSearchValue: (row) => STATUS_STYLES[row.status]?.label ?? row.status,
        render: (_value, row) => {
          const status = STATUS_STYLES[row.status];
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium ${status?.className}`}>
              {status?.label ?? row.status}
            </span>
          );
        },
      },
      {
        key: 'details',
        label: 'Details',
        width: 408,
        getSearchValue: (row) =>
          [row.failureMessage, ...(row.stages ?? []).map((stage) => stage.label)].join(' '),
        render: (_value, row) => {
          const summary = getSummary(row);
          return (
            <span className={`block truncate ${summary.className}`} title={summary.text}>
              {summary.text}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title="Sync history"
      subtitle="Each time Dill synced your data, and whether it worked. Select a sync to see each step."
      maxWidth="900px"
      closeOnOverlayClick={true}>
      <div className="flex flex-col gap-4">
        <DillTable<SupplierSyncEvent>
          columns={columns}
          data={syncEvents}
          rowKey={(row) => row.id}
          search={true}
          searchPlaceholder="Search type, status or details..."
          loading={isLoading}
          emptyMessage="No syncs in this period. Try a longer date range."
          toolbarLeft={
            <button
              onClick={() => setIsDateRangePickerOpen(true)}
              className="flex flex-row items-center GREY_300-BORDER border rounded py-1 px-2 WHITE-BG hover:bg-slate-200">
              <Calendar className="text-slate-500 mr-2" size={20} />
              <div className="text-xs TEXT_PRIMARY-CLR">{`${moment(dateRange.startDate).format(
                'MMM DD, YYYY'
              )}-${moment(dateRange.endDate).format('MMM DD, YYYY')}`}</div>
            </button>
          }
          defaultSort={{ key: 'startedAt', dir: 'desc' }}
          onRowClick={(row) => setSelectedEventId(row.id)}
          striped={false}
          // stronger fill plus a left accent bar, and hover can't wash it out
          rowClassName={(row) =>
            row.id === selectedEvent?.id
              ? 'bg-blue-100 hover:bg-blue-100 shadow-[inset_3px_0_0_#3762FB]'
              : ''
          }
          maxHeight="40vh"
          countLabel={syncEvents.length === 1 ? 'sync' : 'syncs'}
        />
        {/* keyed so the entry animation replays on each selection */}
        {!isLoading && selectedEvent && (
          <SyncEventDetails key={selectedEvent.id} event={selectedEvent} />
        )}
      </div>
      <AppDateRangePickerModal
        open={isDateRangePickerOpen}
        selectedDateRange={dateRange}
        handleClose={() => setIsDateRangePickerOpen(false)}
        onSelectDateRange={({ startDate, endDate }) => {
          setIsDateRangePickerOpen(false);
          if (startDate && endDate) {
            setDateRange({ startDate, endDate });
          }
        }}
      />
    </DillModal>
  );
};
